import { createSelector } from 'reselect';
import { selectChecklists } from 'next/entities/checklists';
import { selectEventNames } from 'next/entities/event-names';
import { selectEventNameLabelsByName } from 'next/entities/event-name-labels';

import {
  selectFeature,
  NPS_RANGE_SM,
  NPS_RANGE_MED,
  NPS_RANGE_LG,
} from 'next/entities/features';
import { selectFlows } from 'next/entities/flows';
import { selectSegments } from 'next/entities/segments';
import {
  selectProfileAttribute,
  selectProfileAttributes,
} from 'next/entities/profile-attributes';
import { selectProfileAttributeLabels } from 'next/entities/profile-attribute-labels';
import { FLOW_TEMPLATE } from 'next/entities/templates';
import { selectTags } from 'next/entities/tags';
import { selectThemes } from 'next/entities/themes';
import { selectGroupAttributes } from 'next/entities/group-attributes';

import { getPropertyDisplayName } from './profile-attributes';

export const selectChecklistTargetingOptions = state =>
  Object.entries(selectChecklists(state) ?? {}).map(
    ([id, { internalName }]) => ({
      label: internalName || id,
      value: id,
    })
  );

export const selectEventTargetingOptions = state => {
  return Object.keys(selectEventNames(state) ?? {})
    .filter(eventName => {
      const label = selectEventNameLabelsByName(state)?.[eventName];
      return label ? label?.showInUI : true;
    })
    .map(eventName => ({
      label: eventName.replace('appcues_custom:', ''),
      value: eventName,
    }));
};

export const selectFlowTargetingOptions = state =>
  Object.values(selectFlows(state) ?? {})
    .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id))
    .flatMap(({ name, id, steps = {} }) =>
      Object.values(steps)
        .filter(({ type }) => !['redirect', 'wait-for-page'].includes(type))
        .sort((a, b) => a.index - b.index)
        .map(({ id: value, index }) => ({
          label: `${name || id} (Step ${index + 1})`,
          value,
        }))
    );

export const selectSegmentTargetingOptions = state =>
  Object.entries(selectSegments(state) ?? {}).map(([id, { name }]) => ({
    label: name || id,
    value: id,
  }));

/**
 * Creates dropdown options from `selectTags` results
 *
 * @param {State} state - Redux state
 * @return {Array.<{ label: string, id: string }>}
 */
export const selectTagsOptions = createSelector(selectTags, tags =>
  Object.values(tags ?? {})
    .map(({ id, name }) => ({
      label: name,
      value: id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
);

/**
 * Creates dropdown options from `selectThemes` results
 *
 * @param {State} state - Redux state
 * @return {Array.<{ label: string, id: string }>}
 */
export const selectThemesOptions = createSelector(selectThemes, themes =>
  Object.values(themes ?? {})
    .map(({ id, name }) => ({
      label: name,
      value: id,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
);

/**
 * Selects defaultTheme and returns as dropdown option
 *
 * @param {State} state - Redux state
 * @return {Object.<label, value>|null} returns Option object
 */
export const selectDefaultThemeOption = createSelector(selectThemes, themes => {
  if (themes) {
    const { name, id } =
      Object.values(themes).find(theme => theme.isDefault) || {};
    return id
      ? {
          label: name,
          value: id,
        }
      : null;
  }

  return null;
});

const ATTRIBUTE_DENYLIST = new Set(['_ABGroup', '_testContentId']);

/**
 * Merges profile attributes & labels to return dropdown options
 *
 * @param {State} state - Redux state
 * @return {Object} Dropdown options formatted for react-select
 */

export const selectProfileAttributeOptions = createSelector(
  [selectProfileAttributes, selectProfileAttributeLabels],
  (attributes, labels) =>
    Object.values(
      Object.keys(attributes ?? {})
        .filter(name => !ATTRIBUTE_DENYLIST.has(name))
        .filter(name => {
          const { showInUI = true } = labels?.[name] || {};
          return Boolean(showInUI);
        })
        .map(name => {
          const { label } = labels?.[name] || {};

          return {
            label: label || getPropertyDisplayName(name),
            value: name,
          };
        })
        .reduce(
          (acc, option) => {
            const { value } = option;

            if (/^(_deviceType|_operatingSystem|_browser)/.test(value)) {
              acc.device.options.push(option);
            } else if (value.startsWith('_appcuesForm_')) {
              acc.device.options.push(option);
            } else if (value.startsWith('_appcuesSatisfaction_')) {
              acc.satisfaction.options.push(option);
            } else if (value.startsWith('_')) {
              acc.auto.options.push(option);
            } else {
              acc.custom.options.push(option);
            }

            return acc;
          },
          {
            custom: {
              label: 'CUSTOM PROPERTIES',
              options: [],
              order: 1,
            },
            form: {
              label: 'FORM RESPONSES',
              options: [],
              order: 2,
            },
            device: {
              label: 'DEVICE PROPERTIES',
              options: [],
              order: 3,
            },
            satisfaction: {
              label: 'USER SATISFACTION',
              options: [],
              order: 4,
            },
            auto: {
              label: 'AUTO-PROPERTIES',
              options: [],
              order: 5,
            },
          }
        )
    )
);

export const selectGroupAttributeOptions = state =>
  Object.keys(selectGroupAttributes(state) ?? {})
    .sort((a, b) => a.localeCompare(b))
    .map(attribute => ({
      label: attribute,
      value: attribute,
    }));

/**
 * Select range options type based on account gates
 *
 * @return {string|null} Range type or null
 */
export const selectDateRangeType = createSelector(
  state => selectFeature(state, NPS_RANGE_SM),
  state => selectFeature(state, NPS_RANGE_MED),
  state => selectFeature(state, NPS_RANGE_LG),
  (small, medium, large) => {
    switch (true) {
      case large:
        return 'large';
      case medium:
        return 'medium';
      case small:
        return 'small';
      default:
        return null;
    }
  }
);

/**
 * Select how many days back a customer can view event data historically
 *
 * @return {number} Number of days from today
 */
export const selectHistoricalLimit = createSelector(
  selectDateRangeType,
  type => {
    switch (type) {
      case 'large':
        return 30 * 36;
      case 'medium':
        return 30 * 24;
      case 'small':
        return 30 * 9;
      default:
        return 30;
    }
  }
);
export const selectCurrentPageUrlOptions = state => {
  const currentPages = selectProfileAttribute(state, '_currentPageUrl') || {};

  return Object.keys(currentPages).map(url => ({ label: url, value: url }));
};

/**
 * Select list of flow template for the flow template dropdown
 *
 * @returns {Object} template
 */
export const selectTemplateOptions = () => {
  return Object.values(FLOW_TEMPLATE).map(
    ({ label, description, value, screenshotUrl, size }) => {
      return { label, description, value, screenshotUrl, size };
    }
  );
};

const transformSuggestions = attributes =>
  Object.entries(attributes ?? {}).reduce((acc, [name, values]) => {
    acc[name] = Object.keys(values);
    return acc;
  }, {});

export const selectProfileAttributeSuggestions = createSelector(
  selectProfileAttributes,
  transformSuggestions
);

export const selectGroupAttributeSuggestions = createSelector(
  selectGroupAttributes,
  transformSuggestions
);
